import { track } from 'src/lib/analytics/utils';
import type {
  BasePaymentAnalytics,
  ProviderPaymentAnalytics,
} from './analytics.types';
import type { WebPaymentMethod, WebPurchaseProvider } from '../iap.types';

/**
 * Web payment UI was opened
 */
export function trackWebPayOpen(
  props: Omit<
    BasePaymentAnalytics,
    'elapsedSecondsOpen' | 'instanceSelectSequence'
  >,
) {
  track('WebPayOpen', {
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
  });
}

/**
 * Web payment age confirm checkbox was tapped
 */
export function trackWebPayAgeConfirm(
  props: BasePaymentAnalytics &
    ProviderPaymentAnalytics & { accepted: boolean },
) {
  track('WebPayAgeConfirm', {
    // Specific property for Age Confirm
    accepted: props.accepted,
    // Provider analytics set
    provider: props.purchaseProvider as WebPurchaseProvider,
    providerMethod: props.purchaseProviderMethod as WebPaymentMethod,
    elapsedSecondsSelect: props.elapsedSecondsSelect,
    hasAgeCheckbox: props.hasAgeCheckbox,
    purchaseID: props.purchaseID,
    internalTransactionId: props.internalTransactionId,
    isMethodPromoted: props.isMethodPromoted,
    rankInMethodList: props.rankInMethodList,
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
    elapsedSecondsOpen: props.elapsedSecondsOpen,
    instanceSelectSequence: props.instanceSelectSequence,
  });
}

/**
 * Web payment start
 */
export function trackWebPayPurchaseStart(
  props: BasePaymentAnalytics & ProviderPaymentAnalytics,
) {
  track('WebPayPurchaseStart', {
    // Provider analytics set
    provider: props.purchaseProvider as WebPurchaseProvider,
    providerMethod: props.purchaseProviderMethod as WebPaymentMethod,
    elapsedSecondsSelect: props.elapsedSecondsSelect,
    hasAgeCheckbox: props.hasAgeCheckbox,
    purchaseID: props.purchaseID,
    internalTransactionId: props.internalTransactionId,
    isMethodPromoted: props.isMethodPromoted,
    rankInMethodList: props.rankInMethodList,
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
    elapsedSecondsOpen: props.elapsedSecondsOpen,
    instanceSelectSequence: props.instanceSelectSequence,
    // For method counters
    allTimeSuccessSequenceForMethod: props.allTimeSuccessSequenceForMethod,
    sessionSuccessSequenceForMethod: props.sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod: props.instanceSelectSequenceForMethod,
  });
}

/**
 * Track when user manually select to close the web payment UI, before close confirmation
 */
export function trackWebPayCloseStart(
  props: BasePaymentAnalytics & Partial<ProviderPaymentAnalytics>,
) {
  track('WebPayCloseStart', {
    // Provider analytics set
    provider: props.purchaseProvider as WebPurchaseProvider,
    providerMethod: props.purchaseProviderMethod as WebPaymentMethod,
    elapsedSecondsSelect: props.elapsedSecondsSelect,
    hasAgeCheckbox: props.hasAgeCheckbox,
    purchaseID: props.purchaseID,
    internalTransactionId: props.internalTransactionId,
    isMethodPromoted: props.isMethodPromoted,
    rankInMethodList: props.rankInMethodList,
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
    elapsedSecondsOpen: props.elapsedSecondsOpen,
    instanceSelectSequence: props.instanceSelectSequence,
    // For method counters
    allTimeSuccessSequenceForMethod: props.allTimeSuccessSequenceForMethod,
    sessionSuccessSequenceForMethod: props.sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod: props.instanceSelectSequenceForMethod,
  });
}

/**
 * Track when user manually cancel the confirmation to close the web payment UI
 */
export function trackWebPayCloseCancel(
  props: BasePaymentAnalytics & Partial<ProviderPaymentAnalytics>,
) {
  track('WebPayCloseCancel', {
    // Provider analytics set
    provider: props.purchaseProvider as WebPurchaseProvider,
    providerMethod: props.purchaseProviderMethod as WebPaymentMethod,
    elapsedSecondsSelect: props.elapsedSecondsSelect,
    hasAgeCheckbox: props.hasAgeCheckbox,
    purchaseID: props.purchaseID,
    internalTransactionId: props.internalTransactionId,
    isMethodPromoted: props.isMethodPromoted,
    rankInMethodList: props.rankInMethodList,
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
    elapsedSecondsOpen: props.elapsedSecondsOpen,
    instanceSelectSequence: props.instanceSelectSequence,
    // For method counters
    allTimeSuccessSequenceForMethod: props.allTimeSuccessSequenceForMethod,
    sessionSuccessSequenceForMethod: props.sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod: props.instanceSelectSequenceForMethod,
  });
}

/**
 * Web payment UI was closed without purchase
 */
export function trackWebPayClose(
  props: BasePaymentAnalytics & Partial<ProviderPaymentAnalytics>,
) {
  track('WebPayClose', {
    // Provider analytics set
    provider: props.purchaseProvider as WebPurchaseProvider,
    providerMethod: props.purchaseProviderMethod as WebPaymentMethod,
    elapsedSecondsSelect: props.elapsedSecondsSelect,
    hasAgeCheckbox: props.hasAgeCheckbox,
    purchaseID: props.purchaseID,
    internalTransactionId: props.internalTransactionId,
    isMethodPromoted: props.isMethodPromoted,
    rankInMethodList: props.rankInMethodList,
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
    elapsedSecondsOpen: props.elapsedSecondsOpen,
    instanceSelectSequence: props.instanceSelectSequence,
    // For method counters
    allTimeSuccessSequenceForMethod: props.allTimeSuccessSequenceForMethod,
    sessionSuccessSequenceForMethod: props.sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod: props.instanceSelectSequenceForMethod,
  });
}

/**
 * Web payment failed
 */
export function trackWebPayPurchaseFailure(
  props: { errorCode: string; errorMessage: string } & BasePaymentAnalytics &
    Partial<ProviderPaymentAnalytics> & {
      elapsedSeconds?: number;
      elapsedSecondsMethod?: number;
    },
) {
  track('WebPayPurchaseFailure', {
    // Specific property for Purchase Failure
    elapsedSeconds: props.elapsedSeconds,
    elapsedSecondsMethod: props.elapsedSecondsMethod,
    // Specific property for Purchase Failure
    errorCode: props.errorCode,
    errorMessage: props.errorMessage,
    // Provider analytics set
    provider: props.purchaseProvider as WebPurchaseProvider,
    providerMethod: props.purchaseProviderMethod as WebPaymentMethod,
    elapsedSecondsSelect: props.elapsedSecondsSelect,
    hasAgeCheckbox: props.hasAgeCheckbox,
    purchaseID: props.purchaseID,
    internalTransactionId: props.internalTransactionId,
    isMethodPromoted: props.isMethodPromoted,
    rankInMethodList: props.rankInMethodList,
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
    elapsedSecondsOpen: props.elapsedSecondsOpen,
    instanceSelectSequence: props.instanceSelectSequence,
    // For method counters
    allTimeSuccessSequenceForMethod: props.allTimeSuccessSequenceForMethod,
    sessionSuccessSequenceForMethod: props.sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod: props.instanceSelectSequenceForMethod,
  });
}

/**
 * Web payment success
 */
export function trackWebPayPurchaseSuccess(
  props: BasePaymentAnalytics &
    ProviderPaymentAnalytics & {
      elapsedSeconds?: number;
      elapsedSecondsMethod?: number;
    },
) {
  track('WebPayPurchaseSuccess', {
    // Specific property for Purchase Success
    elapsedSeconds: props.elapsedSeconds,
    elapsedSecondsMethod: props.elapsedSecondsMethod,
    // Provider analytics set
    provider: props.purchaseProvider as WebPurchaseProvider,
    providerMethod: props.purchaseProviderMethod as WebPaymentMethod,
    elapsedSecondsSelect: props.elapsedSecondsSelect,
    hasAgeCheckbox: props.hasAgeCheckbox,
    purchaseID: props.purchaseID,
    internalTransactionId: props.internalTransactionId,
    isMethodPromoted: props.isMethodPromoted,
    rankInMethodList: props.rankInMethodList,
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
    elapsedSecondsOpen: props.elapsedSecondsOpen,
    instanceSelectSequence: props.instanceSelectSequence,
    // For method counters
    allTimeSuccessSequenceForMethod: props.allTimeSuccessSequenceForMethod,
    sessionSuccessSequenceForMethod: props.sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod: props.instanceSelectSequenceForMethod,
  });
}

/**
 * The user clicked on non currently selected payment method
 */
export function trackWebPayMethodSelect(
  props: BasePaymentAnalytics & ProviderPaymentAnalytics,
) {
  track('WebPayMethodSelect', {
    // Provider analytics set
    provider: props.purchaseProvider as WebPurchaseProvider,
    providerMethod: props.purchaseProviderMethod as WebPaymentMethod,
    elapsedSecondsSelect: props.elapsedSecondsSelect,
    hasAgeCheckbox: props.hasAgeCheckbox,
    purchaseID: props.purchaseID,
    internalTransactionId: props.internalTransactionId,
    isMethodPromoted: props.isMethodPromoted,
    rankInMethodList: props.rankInMethodList,
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
    elapsedSecondsOpen: props.elapsedSecondsOpen,
    instanceSelectSequence: props.instanceSelectSequence,
    // For method counters
    allTimeSuccessSequenceForMethod: props.allTimeSuccessSequenceForMethod,
    sessionSuccessSequenceForMethod: props.sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod: props.instanceSelectSequenceForMethod,
  });
}

/**
 * The user returned to the main view from a selected method
 */
export function trackWebPayMethodCancel(
  props: BasePaymentAnalytics & ProviderPaymentAnalytics,
) {
  track('WebPayMethodCancel', {
    // Provider analytics set
    provider: props.purchaseProvider as WebPurchaseProvider,
    providerMethod: props.purchaseProviderMethod as WebPaymentMethod,
    elapsedSecondsSelect: props.elapsedSecondsSelect,
    hasAgeCheckbox: props.hasAgeCheckbox,
    purchaseID: props.purchaseID,
    internalTransactionId: props.internalTransactionId,
    isMethodPromoted: props.isMethodPromoted,
    rankInMethodList: props.rankInMethodList,
    // Base analytics set
    allTimeOpenSequence: props.allTimeOpenSequence,
    allTimeSuccessSequence: props.allTimeSuccessSequence,
    availableProviderMethods: props.availableProviderMethods,
    availableProviders: props.availableProviders,
    productID: props.productID,
    productPrice: props.productPrice,
    sessionOpenSequence: props.sessionOpenSequence,
    sessionSuccessSequence: props.sessionSuccessSequence,
    elapsedSecondsOpen: props.elapsedSecondsOpen,
    instanceSelectSequence: props.instanceSelectSequence,
    // For method counters
    allTimeSuccessSequenceForMethod: props.allTimeSuccessSequenceForMethod,
    sessionSuccessSequenceForMethod: props.sessionSuccessSequenceForMethod,
    instanceSelectSequenceForMethod: props.instanceSelectSequenceForMethod,
  });
}
